import { useState, useEffect, useRef, useCallback } from "react";
import { Sparkles, X, Send, Plus, Trash2, MessageSquare, ChevronLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Portal } from "@/components/ui/portal";

/**
 * Floating AI Assistant widget for admin + management.
 * Bottom-right pill button → slide-in side panel.
 * Streams responses over SSE (POST /api/ai/chat) with fetch + ReadableStream.
 */
export default function AIAssistant({ user }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat"); // 'chat' | 'sessions'
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamBuf, setStreamBuf] = useState("");
  const [loadingSessions, setLoadingSessions] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  const allowed = user && (user.role === "admin" || user.role === "management");

  const loadSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const { data } = await api.get("/ai/sessions");
      setSessions(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  const loadMessages = useCallback(async (sid) => {
    try {
      const { data } = await api.get(`/ai/sessions/${sid}/messages`);
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, []);

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    const { data } = await api.post("/ai/sessions", { title: "Yeni sohbet" });
    setSessionId(data.id);
    setMessages([]);
    // refresh side panel
    loadSessions();
    return data.id;
  }, [sessionId, loadSessions]);

  useEffect(() => {
    if (open && allowed) loadSessions();
  }, [open, allowed, loadSessions]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamBuf]);

  const startNewChat = () => {
    // abort any in-flight stream
    if (abortRef.current) abortRef.current.abort();
    setStreaming(false);
    setStreamBuf("");
    setSessionId(null);
    setMessages([]);
    setInput("");
    setView("chat");
  };

  const openSession = async (sid) => {
    setSessionId(sid);
    setStreamBuf("");
    await loadMessages(sid);
    setView("chat");
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Bu sohbet silinsin mi?")) return;
    try {
      await api.delete(`/ai/sessions/${sid}`);
      if (sessionId === sid) startNewChat();
      await loadSessions();
      toast.success("Sohbet silindi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    try {
      const sid = await ensureSession();
      // Optimistic UI: append user message immediately
      setMessages((prev) => [
        ...prev,
        { id: `tmp-${Date.now()}`, role: "user", content: text, created_at: new Date().toISOString() },
      ]);
      setStreaming(true);
      setStreamBuf("");

      const ctrl = new AbortController();
      abortRef.current = ctrl;

      const token = localStorage.getItem("akb_token");
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ session_id: sid, message: text }),
        signal: ctrl.signal,
      });

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let assembled = "";

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const frame = buf.slice(0, idx).trim();
          buf = buf.slice(idx + 2);
          if (!frame.startsWith("data:")) continue;
          const payload = frame.replace(/^data:\s*/, "");
          try {
            const obj = JSON.parse(payload);
            if (obj.t) {
              assembled += obj.t;
              setStreamBuf(assembled);
            } else if (obj.error) {
              throw new Error(obj.error);
            } else if (obj.done) {
              // done
            }
          } catch (parseErr) {
            // ignore malformed
          }
        }
      }

      // Finalise: append assistant message and clear stream buf
      if (assembled.trim()) {
        setMessages((prev) => [
          ...prev,
          {
            id: `asst-${Date.now()}`,
            role: "assistant",
            content: assembled,
            created_at: new Date().toISOString(),
          },
        ]);
      }
      setStreamBuf("");
      // refresh sessions in background (title may have updated)
      loadSessions();
    } catch (err) {
      if (err.name !== "AbortError") {
        toast.error(err?.message || "AI yanıtı alınamadı");
      }
      setStreamBuf("");
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!allowed) return null;

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          data-testid="ai-assistant-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-primary text-primary-foreground shadow-floating px-4 py-3 apple-ease hover:scale-105 active:scale-95"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
          aria-label="AI Asistan"
        >
          <Sparkles className="w-5 h-5" />
          <span className="font-semibold text-sm hidden sm:inline">AI Asistan</span>
        </button>
      )}

      {/* Side panel */}
      {open && (
        <Portal>
        <div
          className="fixed inset-0 z-50 flex justify-end animate-apple-fade"
          role="dialog" aria-modal="true"
          data-testid="ai-assistant-panel"
        >
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <div
            className="relative w-full sm:w-[440px] max-w-full h-full bg-background border-l border-border/60 shadow-floating flex flex-col animate-apple-slide-up"
            style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/60 glass-strong">
              <div className="flex items-center gap-2">
                {view === "sessions" && (
                  <button
                    onClick={() => setView("chat")}
                    className="p-1.5 -ml-1 rounded-full hover:bg-muted apple-ease"
                    data-testid="ai-back-to-chat-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                )}
                <Sparkles className="w-5 h-5 text-primary" />
                <span className="font-semibold tracking-tight text-[15px]">
                  {view === "chat" ? "AI Asistan" : "Sohbetler"}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {view === "chat" && (
                  <>
                    <button
                      onClick={() => setView("sessions")}
                      className="p-1.5 rounded-full hover:bg-muted apple-ease"
                      title="Sohbetler"
                      data-testid="ai-list-sessions-btn"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </button>
                    <button
                      onClick={startNewChat}
                      className="p-1.5 rounded-full hover:bg-muted apple-ease"
                      title="Yeni sohbet"
                      data-testid="ai-new-chat-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="p-1.5 rounded-full hover:bg-muted apple-ease"
                  data-testid="ai-assistant-close-btn"
                  aria-label="Kapat"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            {view === "chat" ? (
              <div className="flex-1 flex flex-col min-h-0">
                <div
                  ref={scrollRef}
                  className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
                  data-testid="ai-messages-scroll"
                >
                  {messages.length === 0 && !streamBuf && (
                    <div className="text-center py-10 text-muted-foreground">
                      <Sparkles className="w-8 h-8 mx-auto mb-3 text-primary/70" />
                      <p className="text-sm font-medium">
                        Yönetici asistanınıza soru sorun
                      </p>
                      <p className="text-xs mt-1 opacity-70">
                        Üretim, personel, satış, süreç iyileştirme ve daha fazlası
                      </p>
                    </div>
                  )}
                  {messages.map((m) => (
                    <MessageBubble key={m.id} m={m} />
                  ))}
                  {streamBuf && (
                    <MessageBubble
                      m={{ id: "streaming", role: "assistant", content: streamBuf }}
                      streaming
                    />
                  )}
                </div>

                {/* Composer */}
                <div className="border-t border-border/60 p-3">
                  <div className="flex items-end gap-2">
                    <textarea
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={onKey}
                      placeholder="Mesajınızı yazın... (Enter = gönder)"
                      className="flex-1 resize-none rounded-2xl border border-border/70 bg-muted/40 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 max-h-28"
                      rows={2}
                      disabled={streaming}
                      data-testid="ai-message-input"
                    />
                    <button
                      onClick={sendMessage}
                      disabled={streaming || !input.trim()}
                      className="rounded-full bg-primary text-primary-foreground p-2.5 disabled:opacity-40 apple-ease active:scale-95"
                      data-testid="ai-send-btn"
                      aria-label="Gönder"
                    >
                      {streaming ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1.5 px-1">
                    Model: gpt-5.6-sol · Yalnızca admin & yönetim erişebilir
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-3 space-y-1" data-testid="ai-sessions-list">
                <button
                  onClick={startNewChat}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-primary/10 text-primary font-medium text-sm hover:bg-primary/15 apple-ease"
                  data-testid="ai-new-session-btn"
                >
                  <Plus className="w-4 h-4" /> Yeni sohbet
                </button>
                {loadingSessions && (
                  <p className="text-xs text-muted-foreground text-center py-4">Yükleniyor...</p>
                )}
                {!loadingSessions && sessions.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-6">
                    Henüz sohbet yok. Yeni bir sohbet başlatın.
                  </p>
                )}
                {sessions.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => openSession(s.id)}
                    className={`group flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer hover:bg-muted/50 apple-ease ${
                      sessionId === s.id ? "bg-muted/60" : ""
                    }`}
                    data-testid={`ai-session-${s.id}`}
                  >
                    <MessageSquare className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{s.title || "Yeni sohbet"}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {(s.updated_at || "").slice(0, 16).replace("T", " ")} ·{" "}
                        {s.message_count || 0} mesaj
                      </p>
                    </div>
                    <button
                      onClick={(e) => deleteSession(s.id, e)}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-destructive/10 text-destructive apple-ease"
                      aria-label="Sil"
                      data-testid={`ai-session-delete-${s.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </Portal>
      )}
    </>
  );
}

function MessageBubble({ m, streaming }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
          isUser
            ? "bg-primary text-primary-foreground rounded-br-md"
            : "bg-muted text-foreground rounded-bl-md"
        }`}
      >
        {m.content}
        {streaming && <span className="inline-block ml-1 w-1.5 h-4 align-middle bg-current opacity-60 animate-pulse" />}
      </div>
    </div>
  );
}
